import type { ListOrdersAdminInput, OrderList as OrderListDto } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { Button, cn, EmptyState, Pagination, tableStyles } from '@/shared/ui';
import { OrderFilters } from './order-filters';
import { OrderRow } from './order-row';

interface OrderListProps {
  readonly data?: OrderListDto;
  readonly initialQuery?: ListOrdersAdminInput;
  readonly onQueryChange: (query: ListOrdersAdminInput) => void;
  readonly onOpen: (id: string) => void;
  readonly pending?: boolean;
  readonly error?: string | null;
  /** true 면 상태 필터 선택칸을 숨긴다(필터를 화면이 고정하는 경우). */
  readonly hideStatusFilter?: boolean;
}

const DEFAULT_QUERY: ListOrdersAdminInput = {
  page: 1,
  limit: 20,
  filter: 'TO_PROCESS',
  search: '',
};

const COLUMNS = [
  { label: '주문 번호' },
  { label: '결제일' },
  { label: '회사·담당자' },
  { label: '상품·수량' },
  { label: '금액', className: 'text-right' },
  { label: '상태' },
  { label: '출고 예정일' },
  { label: '배지' },
] as const;

/**
 * 어드민 주문 목록(주문 설계 §8-5) — 도구줄(필터·검색)·흰 표 카드·페이지 이동. 처음 읽는 중·실패·빈 결과는 카드 안
 * 가운데 안내(EmptyState)로 보이고, 조건을 바꿔 다시 읽는 동안에는 받은 표를 그대로 두고 읽는 중임만 알린다.
 */
export const OrderList = ({
  data,
  initialQuery,
  onQueryChange,
  onOpen,
  pending = false,
  error = null,
  hideStatusFilter = false,
}: OrderListProps): ReactElement => {
  const [query, setQuery] = useState<ListOrdersAdminInput>(initialQuery ?? DEFAULT_QUERY);
  const [search, setSearch] = useState(query.search ?? '');

  const change = (next: ListOrdersAdminInput): void => {
    setQuery(next);
    onQueryChange(next);
  };
  // 주문은 있는데 보던 쪽이 범위를 벗어나 비었으면(처리로 건수가 줄어든 경우) "주문 없음"이 아니라 범위 밖이다.
  const outOfRange =
    data !== undefined &&
    data.items.length === 0 &&
    data.meta.totalPages > 0 &&
    data.meta.page > data.meta.totalPages;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <OrderFilters
          hideStatusFilter={hideStatusFilter}
          filter={query.filter}
          search={search}
          onSearch={setSearch}
          onFilter={(filter) => change({ ...query, filter, search: search.trim(), page: 1 })}
          onSubmit={() => change({ ...query, search: search.trim(), page: 1 })}
        />
        {pending && data ? (
          <p role="status" className="ml-auto text-sm text-muted-foreground">
            불러오는 중…
          </p>
        ) : null}
      </div>

      {data === undefined ? (
        <div className="mt-5">
          {error ? <EmptyState isError>{error}</EmptyState> : <EmptyState>불러오는 중…</EmptyState>}
        </div>
      ) : (
        <>
          {error ? (
            <p role="alert" className="mt-4 text-sm text-destructive">
              {error}
            </p>
          ) : null}

          {outOfRange ? (
            <div className="mt-5 flex flex-col items-center gap-4">
              <EmptyState>페이지 범위를 벗어났습니다.</EmptyState>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => change({ ...query, page: data.meta.totalPages })}
              >
                마지막 페이지로
              </Button>
            </div>
          ) : data.items.length === 0 ? (
            <div className="mt-5">
              <EmptyState>조건에 맞는 주문이 없습니다.</EmptyState>
            </div>
          ) : (
            <div className={cn('mt-5', tableStyles.card)}>
              <table className={tableStyles.table}>
                <thead className={tableStyles.head}>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.label}
                        className={cn(tableStyles.th, 'className' in column && column.className)}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((order) => (
                    <OrderRow key={order.id} order={order} onOpen={onOpen} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {outOfRange ? null : (
            <Pagination
              className="mt-5 justify-center"
              page={data.meta.page}
              totalPages={data.meta.totalPages}
              onPageChange={(page) => change({ ...query, page })}
            />
          )}
        </>
      )}
    </>
  );
};
